
import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import VoidLabelConfirmationDialog from '@/components/Shipping/VoidLabelConfirmationDialog.vue';
import { GET_RECENT_SHIPPER_ACTIVITY } from '@/gql/shipper';
import { VOID_SHIPPING_LABEL, CREATE_RETURN_LABEL } from '@/gql/shipping';
import { getPrinterConfiguration } from '@/services/PrinterConfigurationService';
import { getHardwareService, HardwareIntegrationService } from '@/services/HardwareIntegrationService';
import { getShippingActivitySignalR, ShippingActivityEvent } from '@/services/ShippingActivitySignalRService';
import { printStoredLabel, labelPdfUrl } from '@/services/LabelPdfService';
import UpsLogo from '@/assets/svg-icons/ups-logo.svg';
import FedexLogo from '@/assets/svg-icons/fedex-logo.svg';
import UspsLogo from '@/assets/svg-icons/usps-logo.svg';

interface ShipperActivity {
  activityType: string;
  timestamp: string;
  pickTicketId: number;
  pickTicketNumber: string;
  orderId: number;
  orderNumber: string;
  customerName: string;
  shippingLabelId?: number;
  labelId?: string;
  shipmentId?: string;
  shipStationSalesOrderId?: string;
  carrierCode?: string;
  carrierFriendlyName?: string;
  serviceCode?: string;
  serviceName?: string;
  trackingNumber?: string;
  trackingUrl?: string;
  labelData?: string;
  shippingCost?: number;
  isTestLabel?: boolean;
  isVoided?: boolean;
  isReturnLabel?: boolean;
  hasReturnLabel: boolean;
  returnLabelId?: number;
  returnLabelTrackingNumber?: string;
  returnLabelData?: string;
  itemCount?: number;
  // Multi-package fields
  packageSequence?: number;
  totalPackages?: number;
  isMultiPackage?: boolean;
}

@Component({
  components: {
    VoidLabelConfirmationDialog,
  },
})
export default class RecentActivity extends Vue {
  loading = false;
  activities: ShipperActivity[] = [];
  searchQuery = '';
  startDate: string | null = null;
  endDate: string | null = null;
  startDateMenu = false;
  endDateMenu = false;
  selectedPreset = '7days';
  printerConfigService: any = null;
  hardwareService: HardwareIntegrationService | null = null;
  searchDebounceTimer: number | null = null;
  isSettingPreset = false; // Flag to prevent double-fetch during preset changes

  // Activity type filters
  selectedActivityFilters: string[] = [];
  hideVoided = false;

  // Available activity type filter options
  activityFilterOptions = [
    { value: 'LabelCreated', label: 'Label Created', icon: 'mdi-label', color: 'success' },
    { value: 'LabelVoided', label: 'Label Voided', icon: 'mdi-label-off', color: 'error' },
    { value: 'ReturnLabel', label: 'Return Labels', icon: 'mdi-label-variant', color: 'info' },
    { value: 'PickTicketShipped', label: 'Ready to Ship', icon: 'mdi-truck-check', color: 'primary' },
  ];

  // SignalR real-time updates
  signalRUnsubscribe: (() => void) | null = null;
  signalRRefreshDebounceTimer: number | null = null;

  // Carrier logos
  carrierLogos: Record<string, string> = {
    ups: UpsLogo,
    fedex: FedexLogo,
    fedex_express: FedexLogo,
    fedex_ground: FedexLogo,
    fedex_walleted: FedexLogo,
    usps: UspsLogo,
    stamps_com: UspsLogo,
  };

  // Void dialog
  showVoidDialog = false;
  selectedLabel: ShipperActivity | null = null;
  voidLoading = false;
  voidDialogReturnLabelCount = 0;
  voidDialogOutboundLabelId: string | null = null;

  // Snackbar
  snackbar = {
    show: false,
    text: '',
    color: 'success',
  };

  headers = [
    { text: 'Activity', value: 'activityType', width: '14%' },
    { text: 'Date/Time', value: 'timestamp', width: '12%' },
    { text: 'Order', value: 'orderNumber', width: '8%' },
    { text: 'Pick Ticket', value: 'pickTicketNumber', width: '9%' },
    { text: 'Customer', value: 'customerName', width: '14%' },
    { text: 'Carrier', value: 'carrierCode', width: '12%' },
    { text: 'Tracking #', value: 'trackingNumber', width: '14%' },
    { text: 'Cost', value: 'shippingCost', width: '7%' },
    { text: 'Actions', value: 'actions', sortable: false, width: '6%', align: 'center' },
  ];

  get startDateFormatted(): string {
    if (!this.startDate) return '';
    const [year, month, day] = this.startDate.split('-');
    return `${month}/${day}/${year}`;
  }

  get endDateFormatted(): string {
    if (!this.endDate) return '';
    const [year, month, day] = this.endDate.split('-');
    return `${month}/${day}/${year}`;
  }

  /**
   * Filtered activities based on activity type filters and hide voided toggle
   */
  get filteredActivities(): ShipperActivity[] {
    let result = this.activities;

    // Apply hide voided filter
    if (this.hideVoided) {
      result = result.filter(a => !a.isVoided);
    }

    // Apply activity type filters if any are selected
    if (this.selectedActivityFilters.length > 0) {
      result = result.filter(a => {
        // Return labels are a separate category
        if (a.isReturnLabel) {
          return this.selectedActivityFilters.includes('ReturnLabel');
        }
        // Non-return labels match by activity type
        return this.selectedActivityFilters.includes(a.activityType);
      });
    }

    return result;
  }

  get canViewJumpLinks(): boolean {
    const user = this.$store.state.authentication?.user;
    if (!user) return false;
    return user.isAdmin || user.roles?.some((r: any) => r.name === 'shipper');
  }

  getOrderUrl(orderId: number): string {
    const resolved = this.$router.resolve({ name: 'Order', params: { orderId: String(orderId) } });
    return resolved.href;
  }

  getPickTicketPrintUrl(pickTicketId: number): string {
    const resolved = this.$router.resolve({ name: 'Shipper Pick Ticket Print', params: { pickTicketId: String(pickTicketId) } });
    return resolved.href;
  }

  /**
   * Summary statistics computed from filtered activities
   */
  get summaryStats() {
    const stats = {
      labelsCreated: 0,
      labelsVoided: 0,
      returnLabels: 0,
      readyToShip: 0,
      totalShippingCost: 0,
    };

    for (const activity of this.filteredActivities) {
      if (activity.activityType === 'LabelCreated') {
        if (activity.isReturnLabel) {
          stats.returnLabels++;
        } else {
          stats.labelsCreated++;
        }
        // Add shipping cost for non-voided labels
        if (!activity.isVoided && activity.shippingCost) {
          stats.totalShippingCost += activity.shippingCost;
        }
      } else if (activity.activityType === 'LabelVoided') {
        stats.labelsVoided++;
      } else if (activity.activityType === 'PickTicketShipped') {
        stats.readyToShip++;
      }
    }

    return stats;
  }

  mounted() {
    this.printerConfigService = getPrinterConfiguration();
    this.hardwareService = getHardwareService();
    this.setDatePreset(7); // This will call loadActivity()

    // Subscribe to real-time shipping activity updates via SignalR
    this.signalRUnsubscribe = getShippingActivitySignalR().subscribe(this.onShippingActivityUpdate);
  }

  beforeDestroy() {
    // Clean up debounce timers
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    if (this.signalRRefreshDebounceTimer) {
      clearTimeout(this.signalRRefreshDebounceTimer);
    }
    // Unsubscribe from SignalR
    if (this.signalRUnsubscribe) {
      this.signalRUnsubscribe();
    }
  }

  @Watch('startDate')
  onStartDateChange() {
    // Skip if we're in the middle of setting a preset (will load once at the end)
    if (this.isSettingPreset) return;
    this.loadActivity();
  }

  @Watch('endDate')
  onEndDateChange() {
    // Skip if we're in the middle of setting a preset (will load once at the end)
    if (this.isSettingPreset) return;
    this.loadActivity();
  }

  @Watch('searchQuery')
  onSearchQueryChange() {
    // Debounce search to avoid too many requests while typing
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    this.searchDebounceTimer = window.setTimeout(() => {
      this.loadActivity();
    }, 400);
  }

  setDatePreset(days: number) {
    // Set flag to prevent watchers from triggering multiple loads
    this.isSettingPreset = true;

    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);

    this.startDate = this.formatDateForPicker(start);
    this.endDate = this.formatDateForPicker(end);
    this.selectedPreset = `${days}days`;

    // Clear flag and load once
    this.isSettingPreset = false;
    this.loadActivity();
  }

  /**
   * Set date to today only
   */
  setToday() {
    this.isSettingPreset = true;
    const today = new Date();
    this.startDate = this.formatDateForPicker(today);
    this.endDate = this.formatDateForPicker(today);
    this.selectedPreset = 'today';
    this.isSettingPreset = false;
    this.loadActivity();
  }

  /**
   * Set date to yesterday only
   */
  setYesterday() {
    this.isSettingPreset = true;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.startDate = this.formatDateForPicker(yesterday);
    this.endDate = this.formatDateForPicker(yesterday);
    this.selectedPreset = 'yesterday';
    this.isSettingPreset = false;
    this.loadActivity();
  }

  /**
   * Set date range from January 1st of current year to today
   */
  setYearToDate() {
    this.isSettingPreset = true;
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 1); // January 1st
    this.startDate = this.formatDateForPicker(startOfYear);
    this.endDate = this.formatDateForPicker(today);
    this.selectedPreset = 'ytd';
    this.isSettingPreset = false;
    this.loadActivity();
  }

  /**
   * Toggle activity type filter
   */
  toggleActivityFilter(filterValue: string) {
    const index = this.selectedActivityFilters.indexOf(filterValue);
    if (index === -1) {
      this.selectedActivityFilters.push(filterValue);
    } else {
      this.selectedActivityFilters.splice(index, 1);
    }
  }

  /**
   * Clear all activity type filters
   */
  clearActivityFilters() {
    this.selectedActivityFilters = [];
  }

  /**
   * Check if a filter is active
   */
  isFilterActive(filterValue: string): boolean {
    return this.selectedActivityFilters.includes(filterValue);
  }

  formatDateForPicker(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  async loadActivity() {
    this.loading = true;
    try {
      const variables: any = {
        limit: 100,
      };

      if (this.startDate) {
        variables.startDate = new Date(this.startDate + 'T00:00:00').toISOString();
      }
      if (this.endDate) {
        variables.endDate = new Date(this.endDate + 'T23:59:59').toISOString();
      }
      if (this.searchQuery) {
        variables.search = this.searchQuery;
      }

      const { data } = await this.$apollo.query({
        query: GET_RECENT_SHIPPER_ACTIVITY,
        variables,
        fetchPolicy: 'network-only',
      });

      if (data?.recentShipperActivity) {
        this.activities = data.recentShipperActivity;
      }
    } catch (error) {
      console.error('[RecentActivity] Failed to load activity:', error);
      this.showNotification('Failed to load activity', 'error');
    } finally {
      this.loading = false;
    }
  }

  /**
   * Handle SignalR shipping activity updates.
   * Debounces refreshes to prevent rapid updates.
   */
  onShippingActivityUpdate(event: ShippingActivityEvent) {
    console.log('[RecentActivity] SignalR shipping activity update:', event);

    // Debounce the refresh to prevent rapid updates
    if (this.signalRRefreshDebounceTimer) {
      clearTimeout(this.signalRRefreshDebounceTimer);
    }

    this.signalRRefreshDebounceTimer = window.setTimeout(() => {
      console.log('[RecentActivity] Executing debounced refresh');
      this.loadActivity();
      this.signalRRefreshDebounceTimer = null;
    }, 1000); // 1 second debounce
  }

  getActivityIcon(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'mdi-label-variant' : 'mdi-label';
      case 'LabelVoided':
        return activity.isReturnLabel ? 'mdi-label-variant-outline' : 'mdi-label-off';
      case 'PickTicketShipped':
        return 'mdi-truck-check';
      default:
        return 'mdi-information';
    }
  }

  getActivityColor(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'info' : 'success';
      case 'LabelVoided':
        return 'error';
      case 'PickTicketShipped':
        return 'primary';
      default:
        return 'grey';
    }
  }

  getActivityLabel(activity: any): string {
    switch (activity.activityType) {
      case 'LabelCreated':
        return activity.isReturnLabel ? 'Return Label Created' : 'Label Created';
      case 'LabelVoided':
        return activity.isReturnLabel ? 'Return Label Voided' : 'Label Voided';
      case 'PickTicketShipped':
        return 'Ready to Ship';
      default:
        return activity.activityType;
    }
  }

  getReturnLabelDisabledReason(item: ShipperActivity): string {
    if (!item.labelId) {
      return 'No label has been created';
    }
    if (item.isVoided) {
      return 'Label has been voided';
    }
    if (item.hasReturnLabel) {
      return 'Return label already exists';
    }
    if (item.isTestLabel) {
      return 'Cannot create return label for test labels';
    }
    return 'Return label not available';
  }

  getCarrierLogo(carrierCode: string): string | null {
    if (!carrierCode) return null;
    const code = carrierCode.toLowerCase();
    // Check exact match first
    if (this.carrierLogos[code]) return this.carrierLogos[code];
    // Then check partial matches
    if (code.includes('ups')) return this.carrierLogos.ups;
    if (code.includes('fedex')) return this.carrierLogos.fedex;
    if (code.includes('usps') || code.includes('stamps')) return this.carrierLogos.usps;
    return null;
  }

  formatDate(timestamp: string): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    // Always display in Central Time regardless of browser timezone
    return date.toLocaleDateString('en-US', { timeZone: 'America/Chicago' });
  }

  formatTime(timestamp: string): string {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    // Always display in Central Time regardless of browser timezone
    return date.toLocaleTimeString('en-US', {
      timeZone: 'America/Chicago',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatCurrency(amount: number): string {
    return amount.toFixed(2);
  }

  async printLabel(item: ShipperActivity) {
    if (!item.shippingLabelId && !item.labelData) {
      this.showNotification('No label data available', 'error');
      return;
    }
    if (item.isVoided) {
      this.showNotification('This label was voided', 'error');
      return;
    }

    const config = this.printerConfigService?.getConfiguration();
    const printerId = config?.labelPrinterId;

    if (!printerId) {
      // Open label in new tab as fallback
      this.viewLabel(item.shippingLabelId, item.labelData);
      this.showNotification('No printer configured. Label opened in new tab.', 'warning');
      return;
    }

    try {
      const result = await this.printShippingLabel(item, printerId);
      if (result.success) {
        this.showNotification('Label sent to printer', 'success');
      } else {
        this.showNotification(result.error || 'Failed to print label', 'error');
      }
    } catch (error: any) {
      console.error('[RecentActivity] Print error:', error);
      this.showNotification(error.message || 'Failed to print label', 'error');
    }
  }

  async printReturnLabel(item: ShipperActivity) {
    if (!item.returnLabelId && !item.returnLabelData) {
      this.showNotification('No return label data available', 'error');
      return;
    }

    const config = this.printerConfigService?.getConfiguration();
    const printerId = config?.labelPrinterId;

    if (!printerId) {
      // Open label in new tab as fallback
      this.viewLabel(item.returnLabelId, item.returnLabelData);
      this.showNotification('No printer configured. Return label opened in new tab.', 'warning');
      return;
    }

    try {
      const result = await this.printShippingLabel({ shippingLabelId: item.returnLabelId, labelData: item.returnLabelData }, printerId);
      if (result.success) {
        this.showNotification('Return label sent to printer', 'success');
      } else {
        this.showNotification(result.error || 'Failed to print return label', 'error');
      }
    } catch (error: any) {
      console.error('[RecentActivity] Print return label error:', error);
      this.showNotification(error.message || 'Failed to print return label', 'error');
    }
  }

  openVoidDialog(item: ShipperActivity) {
    this.selectedLabel = item;

    // Count active return labels for the same pick ticket
    this.voidDialogReturnLabelCount = this.activities.filter(a =>
      a.pickTicketId === item.pickTicketId && a.isReturnLabel && !a.isVoided
    ).length;

    // Find outbound label ID for "Void All" when voiding a return label
    if (item.isReturnLabel) {
      const outbound = this.activities.find(a =>
        a.pickTicketId === item.pickTicketId && !a.isReturnLabel && !a.isVoided
      );
      this.voidDialogOutboundLabelId = outbound?.labelId || null;
    } else {
      this.voidDialogOutboundLabelId = null;
    }

    this.showVoidDialog = true;
  }

  async confirmVoidLabel() {
    if (!this.selectedLabel?.labelId) return;

    this.voidLoading = true;
    try {
      const result = await this.voidLabelRequest(this.selectedLabel.labelId);
      if (result.success) {
        this.showNotification(result.message, 'success');
        this.showVoidDialog = false;
        this.loadActivity(); // Refresh the list
      } else {
        this.showNotification(result.message, 'error');
      }
    } catch (error: any) {
      console.error('[RecentActivity] Void error:', error);
      this.showNotification(error.message || 'Failed to void label', 'error');
    } finally {
      this.voidLoading = false;
    }
  }

  async confirmVoidAllLabels(outboundLabelId: string) {
    if (!outboundLabelId) return;

    this.voidLoading = true;
    try {
      // Void the outbound label, which cascades to all sibling packages + return labels
      const result = await this.voidLabelRequest(outboundLabelId);
      if (result.success) {
        this.showNotification('All labels voided successfully', 'success');
        this.showVoidDialog = false;
        this.loadActivity();
      } else {
        this.showNotification(result.message, 'error');
      }
    } catch (error: any) {
      console.error('[RecentActivity] Void all error:', error);
      this.showNotification(error.message || 'Failed to void labels', 'error');
    } finally {
      this.voidLoading = false;
    }
  }

  async createReturnLabelForItem(item: ShipperActivity) {
    if (!item.labelId) return;

    this.loading = true;
    try {
      const result = await this.createReturnLabelRequest(item.labelId);
      if (result) {
        this.showNotification('Return label created successfully', 'success');
        this.loadActivity(); // Refresh the list
      } else {
        this.showNotification('Failed to create return label', 'error');
      }
    } catch (error: any) {
      console.error('[RecentActivity] Create return label error:', error);
      this.showNotification(error.message || 'Failed to create return label', 'error');
    } finally {
      this.loading = false;
    }
  }

  async copyTrackingNumber(trackingNumber: string) {
    const success = await this.copyToClipboard(trackingNumber);
    if (success) {
      this.showNotification('Tracking number copied', 'success');
    } else {
      this.showNotification('Failed to copy tracking number', 'error');
    }
  }

  showNotification(text: string, color: string) {
    this.snackbar.text = text;
    this.snackbar.color = color;
    this.snackbar.show = true;
  }

  // Label operation methods (previously from mixin)
  /** Opens the stored label from TL-OES; falls back to the ShipStation URL only for rows without an id. */
  viewLabel(shippingLabelId?: number | null, labelUrl?: string | null): void {
    if (shippingLabelId) {
      window.open(labelPdfUrl(shippingLabelId), '_blank');
    } else if (labelUrl) {
      window.open(labelUrl, '_blank');
    }
  }

  viewInShipStation(item: ShipperActivity): void {
    if (!item.shipmentId || !item.shipStationSalesOrderId) return;

    const shipmentId = item.shipmentId.replace('se-', '');

    let url: string;
    if (item.isReturnLabel && item.labelId) {
      const returnId = item.labelId.replace('se-', '');
      url = `https://ship14.shipstation.com/returns/standard/outstanding/order/${item.shipStationSalesOrderId}/return/${returnId}?page=1&pageSize=250`;
    } else {
      url = `https://ship14.shipstation.com/orders/shipped/order/${item.shipStationSalesOrderId}/active/${shipmentId}`;
    }

    window.open(url, '_blank');
  }

  getCarrierTrackingUrl(trackingNumber: string, carrierCode: string): string | null {
    if (!trackingNumber || !carrierCode) return null;
    const carrierLower = carrierCode.toLowerCase();

    if (carrierLower.includes('ups')) {
      return `https://www.ups.com/track?tracknum=${encodeURIComponent(trackingNumber)}`;
    } else if (carrierLower.includes('fedex')) {
      return `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(trackingNumber)}`;
    } else if (carrierLower.includes('usps') || carrierLower.includes('stamps')) {
      return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(trackingNumber)}`;
    } else if (carrierLower.includes('dhl')) {
      return `https://www.dhl.com/us-en/home/tracking/tracking-global-forwarding.html?submit=1&tracking-id=${encodeURIComponent(trackingNumber)}`;
    }
    return null;
  }

  /**
   * Get the tracking URL for a shipment item, constructing it from carrier code and tracking number
   * if the stored URL doesn't match the tracking number (fixes multi-package shipment URLs)
   */
  getTrackingUrlForItem(item: ShipperActivity): string | null {
    if (!item.trackingNumber) return null;

    // If stored URL contains this specific tracking number, use it
    if (item.trackingUrl && item.trackingUrl.includes(item.trackingNumber)) {
      return item.trackingUrl;
    }

    // Otherwise, construct URL from carrier code and tracking number
    if (item.carrierCode) {
      const constructedUrl = this.getCarrierTrackingUrl(item.trackingNumber, item.carrierCode);
      if (constructedUrl) {
        return constructedUrl;
      }
    }

    // Fallback to stored URL if we can't construct one
    return item.trackingUrl || null;
  }

  trackPackage(trackingNumber: string, carrierCode: string): void {
    const url = this.getCarrierTrackingUrl(trackingNumber, carrierCode);
    if (url) {
      window.open(url, '_blank');
    }
  }

  async printShippingLabel(label: { shippingLabelId?: number | null; labelData?: string | null }, printerId: string): Promise<{ success: boolean; error?: string }> {
    try {
      if (!this.hardwareService) {
        return { success: false, error: 'Hardware service not available' };
      }
      if (!printerId) {
        return { success: false, error: 'No printer selected' };
      }

      return await printStoredLabel(this.hardwareService, printerId, label, false);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      console.error('[RecentActivity] Print error:', errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  async voidLabelRequest(labelId: string): Promise<{ success: boolean; message: string; returnLabelVoided?: boolean }> {
    try {
      const response = await this.$apollo.mutate({
        mutation: VOID_SHIPPING_LABEL,
        variables: { labelId },
      });

      const result = response.data.voidShippingLabel;
      return result;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to void label';
      console.error('[RecentActivity] Void error:', errorMessage);
      return { success: false, message: errorMessage };
    }
  }

  async createReturnLabelRequest(originalLabelId: string): Promise<any> {
    try {
      const response = await this.$apollo.mutate({
        mutation: CREATE_RETURN_LABEL,
        variables: {
          labelId: originalLabelId,
          returnLabelMessages: null, // Server auto-generates default messages (rep initials + order number)
        },
      });

      return response.data.createReturnLabel;
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create return label';
      console.error('[RecentActivity] Create return label error:', errorMessage);
      return null;
    }
  }

  async copyToClipboard(text: string): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      console.error('[RecentActivity] Failed to copy to clipboard:', error);
      return false;
    }
  }

  /**
   * Export filtered activities to CSV file
   */
  exportToCsv() {
    if (this.filteredActivities.length === 0) {
      this.showNotification('No activities to export', 'warning');
      return;
    }

    // Define CSV headers
    const headers = [
      'Activity Type',
      'Date',
      'Time',
      'Order Number',
      'Pick Ticket',
      'Customer',
      'Carrier',
      'Service',
      'Tracking Number',
      'Package',
      'Shipping Cost',
      'Is Voided',
      'Is Return Label',
      'Is Test Label',
    ];

    // Map activities to CSV rows
    const rows = this.filteredActivities.map(activity => {
      const timestamp = new Date(activity.timestamp);
      return [
        this.getActivityLabel(activity),
        timestamp.toLocaleDateString('en-US', { timeZone: 'America/Chicago' }),
        timestamp.toLocaleTimeString('en-US', { timeZone: 'America/Chicago' }),
        activity.orderNumber || '',
        activity.pickTicketNumber || '',
        activity.customerName || '',
        activity.carrierFriendlyName || activity.carrierCode || '',
        activity.serviceName || activity.serviceCode || '',
        activity.trackingNumber || '',
        activity.isMultiPackage ? `${activity.packageSequence}/${activity.totalPackages}` : '',
        activity.shippingCost ? activity.shippingCost.toFixed(2) : '',
        activity.isVoided ? 'Yes' : 'No',
        activity.isReturnLabel ? 'Yes' : 'No',
        activity.isTestLabel ? 'Yes' : 'No',
      ];
    });

    // Build CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')),
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    // Generate filename with date range
    const startStr = this.startDate ? this.startDate.replace(/-/g, '') : 'all';
    const endStr = this.endDate ? this.endDate.replace(/-/g, '') : 'all';
    const filename = `shipping-activity_${startStr}_to_${endStr}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.showNotification(`Exported ${this.filteredActivities.length} activities to CSV`, 'success');
  }
}
